import React, { useEffect } from "react";
import { useState } from "react";
import { BsChevronDown } from "react-icons/bs";
import useNavigateToProfile from "../../hooks/useNavigateToProfile";
import ExternalLink from "../common/ExternalLink";
import WalletDisplay from "../common/WalletDisplay";
import { contractNetworks } from "../../utils/currencies";
import { HiOutlineLightningBolt, HiOutlineExternalLink } from "react-icons/hi";

const eventDataLabel = {
  Mint: { borderColor: "#8538FF", bgColor: "bg-primary/10", textColor: "text-primary" },
  Transfer: { borderColor: "#0EA4BD", bgColor: "bg-cyan-100", textColor: "text-cyan-600" },
};

const ItemActivityTab = ({ activities, blockchain }) => {
  const [seeAll, setSeeAll] = useState(false);
  const [sortedActivities, setSortedActivities] = useState(activities);

  useEffect(() => {
    setSortedActivities(
      [...activities]?.sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )
    );
  }, [activities]);

  return (
    <div id="activities" className="w-full flex flex-col pt-6">
      {/* Header Section */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-primary to-[#7e7cff] flex items-center justify-center">
          <HiOutlineLightningBolt className="w-5 h-5 text-white" />
        </div>
        <h5 className="text-xl font-bold text-secondary">Activity</h5>
        <span className="ml-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
          {activities?.length || 0} events
        </span>
      </div>
      
      <p className="text-gray-600 leading-relaxed mb-8 max-w-3xl">
        Track all transactions and interactions involving this project's NFT, including
        mints, transfers, and other on-chain activities.
      </p>
      
      {!activities?.length ? (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <svg className="w-20 h-20 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <p className="text-lg font-medium">No activity yet</p>
          <p className="text-sm">Transactions will appear here</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-2xl shadow-md">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-white">
                  <th className="px-4 py-4 text-left font-semibold text-gray-600">Event</th>
                  <th className="px-4 py-4 text-left font-semibold text-gray-600">From</th>
                  <th className="px-4 py-4 text-left font-semibold text-gray-600">To</th>
                  <th className="px-4 py-4 text-left font-semibold text-gray-600">Date</th>
                  <th className="px-4 py-4 text-center font-semibold text-gray-600">Tx</th>
                </tr>
              </thead>
              <tbody>
                {sortedActivities
                  .slice(0, seeAll ? undefined : 5)
                  ?.map((item, index) => {
                    const eventStyle = eventDataLabel?.[item?.event] || { 
                      bgColor: "bg-gray-100", 
                      textColor: "text-gray-600" 
                    };
                    
                    return (
                      <tr
                        key={index}
                        className={`transition-colors ${index % 2 === 0 ? 'bg-white hover:bg-gray-50' : 'bg-gray-50/50 hover:bg-gray-100'}`}
                      >
                        <td className="px-4 py-4">
                          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${eventStyle.bgColor} ${eventStyle.textColor}`}>
                            {item?.event === "Mint" && (
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                            )}
                            {item?.event === "Transfer" && (
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                              </svg>
                            )}
                            {item?.event}
                          </span>
                        </td>
                        
                        <td className="px-4 py-4">
                          <ExternalLink
                            href={`${contractNetworks?.[blockchain]?.explorer}/address/${item?.from}`}
                            className="text-gray-600 hover:text-primary transition-colors font-mono text-sm flex items-center gap-1"
                          >
                            <WalletDisplay address={item?.from} />
                          </ExternalLink>
                        </td>
                        
                        <td className="px-4 py-4">
                          <ExternalLink
                            href={`${contractNetworks?.[blockchain]?.explorer}/address/${item?.to}`}
                            className="text-gray-600 hover:text-primary transition-colors font-mono text-sm flex items-center gap-1"
                          >
                            <WalletDisplay address={item?.to} />
                          </ExternalLink>
                        </td>

                        <td className="px-4 py-4 text-gray-600 text-sm">
                          {new Date(item?.updatedAt).toLocaleDateString()}
                        </td>
                        
                        <td className="px-4 py-4 text-center">
                          <ExternalLink
                            href={`${contractNetworks?.[blockchain]?.explorer}/tx/${item?.txHash}`}
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                          >
                            <HiOutlineExternalLink className="w-4 h-4" />
                          </ExternalLink>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          
          {activities?.length > 5 && (
            <div className="flex justify-center mt-6">
              <button
                onClick={() => setSeeAll(!seeAll)}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary/10 text-primary font-medium hover:bg-primary/20 transition-colors"
              >
                {seeAll ? "Show less" : `Show all ${activities.length} activities`}
                <BsChevronDown
                  className={`transition-transform duration-300 ${seeAll ? "rotate-180" : ""}`}
                />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ItemActivityTab;
